import React, { useEffect, useMemo, useState } from "react";

export default function DivisionCheckIn({ token, user }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [stats, setStats] = useState({ total_members: 0, checked_in: 0 });
  const [formDate, setFormDate] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [formOverride, setFormOverride] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleMessage, setScheduleMessage] = useState("");
  const [scheduleError, setScheduleError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 400);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    let isActive = true;
    const fetchMembers = async () => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams();
        if (debouncedSearch) {
          params.set("q", debouncedSearch);
        }
        params.set("_", String(reloadKey));
        const url = `https://smartcoreapi.ssfkerala.org/divisions/checkin.php${params.toString() ? `?${params.toString()}` : ""}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
          throw new Error("Failed to load members");
        }
        const json = await res.json();
        if (!isActive) return;
        setMembers(json.members ?? []);
        setSchedule(json.schedule ?? null);
        setCheckinOpen(Boolean(json.checkin_open));
        setStats(json.stats ?? { total_members: 0, checked_in: 0 });
        if (json.schedule) {
          setFormDate(json.schedule.program_date ?? "");
          setFormNotes(json.schedule.notes ?? "");
          setFormOverride(Boolean(Number(json.schedule.override_enabled || 0)));
        }
      } catch (err) {
        if (!isActive) return;
        setError(err.message || "Failed to load members");
        setMembers([]);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchMembers();
    return () => {
      isActive = false;
    };
  }, [token, debouncedSearch, reloadKey]);

  const handleToggle = async (member) => {
    const nextState = member.checked_in ? 0 : 1;
    setUpdatingId(member.id);
    setError("");
    setScheduleMessage("");
    try {
      const res = await fetch("https://smartcoreapi.ssfkerala.org/divisions/checkin.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ member_id: member.id, checked_in: nextState })
      });
      if (!res.ok) {
        throw new Error("Failed to update check-in status");
      }
      const json = await res.json();
      setMembers((prev) =>
        prev.map((m) =>
          m.id === member.id
            ? {
                ...m,
                checked_in: json.checked_in ? 1 : 0,
                checked_in_at: json.checked_in_at || null,
                checked_in_by: json.checked_in_by || null
              }
            : m
        )
      );
      setScheduleMessage("");
    } catch (err) {
      setError(err.message || "Failed to update check-in status");
      setScheduleMessage(err.message || "Failed to update check-in status");
    } finally {
      setUpdatingId(null);
      setReloadKey((k) => k + 1);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) {
        params.set("q", debouncedSearch);
      }
      params.set("format", "csv");
      params.set("_", String(Date.now()));
      const url = `https://smartcoreapi.ssfkerala.org/divisions/checkin.php${params.toString() ? `?${params.toString()}` : ""}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        throw new Error("Failed to download export");
      }
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      link.href = downloadUrl;
      link.download = `division-checkin-${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      setError(err.message || "Failed to download export");
    } finally {
      setExporting(false);
    }
  };

  const summary = useMemo(() => {
    const total = stats?.total_members ?? members.length;
    const checkedIn = stats?.checked_in ?? members.filter((m) => m.checked_in).length;
    return { total, checkedIn };
  }, [stats, members]);

  const handleScheduleSave = async (e) => {
    e.preventDefault();
    setScheduleSaving(true);
    setScheduleError("");
    setScheduleMessage("");
    try {
      const res = await fetch("https://smartcoreapi.ssfkerala.org/divisions/checkin.php", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          program_date: formDate,
          notes: formNotes,
          override_enabled: formOverride ? 1 : 0
        })
      });
      if (!res.ok) {
        throw new Error("Failed to save schedule");
      }
      const json = await res.json();
      setSchedule(json.schedule ?? null);
      setCheckinOpen(Boolean(json.checkin_open));
      setScheduleMessage("Program schedule updated successfully");
      setReloadKey((k) => k + 1);
    } catch (err) {
      setScheduleError(err.message || "Failed to save schedule");
    } finally {
      setScheduleSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold text-gray-900">Check-In</h2>
            <p className="text-sm text-gray-500">{user.orgname}</p>
            <div className="text-xs text-gray-500">
              {schedule?.program_date
                ? `Program date: ${schedule.program_date}`
                : "Program date not set"}
            </div>
            <div className="text-xs font-medium">
              {checkinOpen ? (
                <span className="text-green-600">Check-in is open</span>
              ) : schedule?.program_date ? (
                <span className="text-orange-600">Check-in locked until {schedule.program_date}</span>
              ) : (
                <span className="text-red-600">Set a program date to enable check-in</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <span className="rounded-lg bg-gray-100 px-3 py-1">{`Checked in: ${summary.checkedIn}`}</span>
            <span className="rounded-lg bg-gray-100 px-3 py-1">{`Total: ${summary.total}`}</span>
          </div>
        </div>

        <form onSubmit={handleScheduleSave} className="mt-4 grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
          <label className="text-sm font-medium text-gray-700">
            Program Date
            <input
              type="date"
              value={formDate}
              onChange={(e) => setFormDate(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              required
            />
          </label>
          <label className="text-sm font-medium text-gray-700">
            Notes
            <input
              value={formNotes}
              onChange={(e) => setFormNotes(e.target.value)}
              placeholder="Optional notes"
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </label>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              checked={formOverride}
              onChange={(e) => setFormOverride(e.target.checked)}
              className="h-4 w-4"
            />
            Allow early check-in (override)
          </label>
          <div className="flex flex-wrap items-center gap-3 text-sm sm:col-span-3">
            <button
              type="submit"
              disabled={scheduleSaving}
              className="rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition hover:bg-blue-700 disabled:opacity-60"
            >
              {scheduleSaving ? "Saving..." : "Save Schedule"}
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className="rounded-lg bg-emerald-600 px-4 py-2 font-medium text-white transition hover:bg-emerald-700 disabled:opacity-60"
            >
              {exporting ? "Preparing export..." : "Download CSV"}
            </button>
            {scheduleMessage && <span className="text-green-600">{scheduleMessage}</span>}
            {scheduleError && <span className="text-red-600">{scheduleError}</span>}
          </div>
        </form>

        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone, unit or sector"
            className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {error && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

      {!checkinOpen && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          Check-in actions are disabled until the scheduled program date or override is enabled.
        </div>
      )}

      {loading ? (
        <div className="bg-white py-12 text-center shadow-sm">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
        </div>
      ) : members.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-white py-12 text-center text-gray-500">
          No members found.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {members.map((member) => {
            const checked = !!member.checked_in;
            return (
              <div
                key={member.id}
                className={`rounded-2xl border p-4 shadow-sm space-y-2 transition ${
                  checked ? "bg-green-50/80 border-green-200" : "bg-white border-gray-200"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold text-gray-900">{member.name}</div>
                    <div className="text-xs text-gray-500">{member.phone || "No phone"}</div>
                  </div>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      checked ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {checked ? "Checked in" : "Pending"}
                  </span>
                </div>
                <div className="mt-3 space-y-1 text-sm text-gray-600">
                  <div>{`Sector: ${member.sector_name || "-"}`}</div>
                  <div>{`Unit: ${member.unit_name || "-"}`}</div>
                  {member.checked_in_at && (
                    <div className="text-xs text-gray-500">
                      {`Checked in at ${member.checked_in_at}`}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleToggle(member)}
                  disabled={updatingId === member.id || !checkinOpen}
                  className={`mt-4 w-full rounded-lg px-4 py-2 text-sm font-medium transition ${
                    checked
                      ? "bg-green-600 text-white hover:bg-green-700 disabled:opacity-70"
                      : "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-70"
                  }`}
                >
                  {updatingId === member.id
                    ? "Updating..."
                    : checked
                      ? "Undo Check-in"
                      : checkinOpen
                        ? "Mark Check-in"
                        : "Check-in Locked"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
